/**
 * Spreadsheet formula engine (pure, no DOM).
 *
 * Evaluates the `=`-prefixed formulas of the Workbook model (`office-io.ts`)
 * entirely client-side so the grid recomputes as the user types. Scope is the
 * pragmatic 80/20 of a sheet MVP:
 *
 *  · literals: numbers, "strings", TRUE/FALSE
 *  · cell refs `A1` / `$B$2`, cross-sheet refs `Sheet2!A1` / `'My Sheet'!A1`
 *    (quoted when the name has spaces/special chars, `''` escapes a quote)
 *  · ranges `A1:B3` (also sheet-prefixed) as arguments to aggregate functions
 *  · the `#REF!` literal (what structural edits leave behind for dead refs)
 *  · operators `+ - * / ^ %` (unary minus), `&` (concat), comparisons
 *    `= <> < <= > >=`, parentheses
 *  · functions: SUM, AVERAGE, MIN, MAX, COUNT, COUNTA, IF, ABS, ROUND
 *  · errors as values (Excel-style): #DIV/0!, #VALUE!, #NAME?, #REF!, #CYCLE!
 *
 * Dependency handling: `computeSheet` memoises each formula cell per pass and
 * detects reference cycles with an evaluation stack (any cell participating
 * in a cycle shows #CYCLE! instead of hanging).
 */

// ── Values ───────────────────────────────────────────────────────────────────

/** Scalar result of evaluating an expression. `null` = empty cell. */
export type Scalar = number | string | boolean | null;

export class FormulaError extends Error {
  /** The Excel-style error code (also the message), e.g. `#DIV/0!`. */
  get code(): string {
    return this.message;
  }
}

const ERR = {
  div0: "#DIV/0!",
  value: "#VALUE!",
  name: "#NAME?",
  ref: "#REF!",
  cycle: "#CYCLE!",
  parse: "#ERROR!",
} as const;

// ── Cell addressing ──────────────────────────────────────────────────────────

export interface CellAddr {
  /** 0-based row. */
  row: number;
  /** 0-based column. */
  col: number;
}

/** `0 → "A"`, `25 → "Z"`, `26 → "AA"` … */
export function colName(col: number): string {
  let name = "";
  let n = col;
  for (;;) {
    name = String.fromCodePoint(65 + (n % 26)) + name;
    n = Math.floor(n / 26) - 1;
    if (n < 0) {
      return name;
    }
  }
}

/** `"A1"`/`"$a$1"` → `{row:0, col:0}`; `null` when not a cell ref. */
export function parseCellRef(ref: string): CellAddr | null {
  const m = /^\$?([A-Za-z]{1,3})\$?([0-9]{1,7})$/.exec(ref.trim());
  if (!m) {
    return null;
  }
  const letters = (m[1] ?? "").toUpperCase();
  let col = 0;
  for (const ch of letters) {
    col = col * 26 + ((ch.codePointAt(0) ?? 0) - 64);
  }
  const row = Number(m[2]) - 1;
  return { row, col: col - 1 };
}

export function cellName(addr: CellAddr): string {
  return `${colName(addr.col)}${addr.row + 1}`;
}

// ── Tokenizer ────────────────────────────────────────────────────────────────

type Token =
  | { t: "num"; v: number }
  | { t: "str"; v: string }
  | { t: "ident"; v: string } // function name, TRUE/FALSE or cell ref
  | { t: "qname"; v: string } // 'quoted sheet name' (before a `!`)
  | { t: "errlit"; v: string } // an error literal, e.g. #REF!
  | { t: "op"; v: string }
  | { t: "lparen" }
  | { t: "rparen" }
  | { t: "comma" }
  | { t: "colon" }
  | { t: "bang" };

function tokenize(src: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i]!;
    if (ch === " " || ch === "\t" || ch === "\n") {
      i++;
      continue;
    }
    if (/[0-9.]/.test(ch)) {
      const m = /^[0-9]*\.?[0-9]+(?:[eE][+-]?[0-9]+)?/.exec(src.slice(i));
      if (!m) {
        throw new FormulaError(ERR.parse);
      }
      tokens.push({ t: "num", v: Number(m[0]) });
      i += m[0].length;
      continue;
    }
    if (ch === '"') {
      let out = "";
      i++;
      for (;;) {
        if (i >= src.length) {
          throw new FormulaError(ERR.parse);
        }
        if (src[i] === '"') {
          // Doubled quote = escaped quote (Excel convention).
          if (src[i + 1] === '"') {
            out += '"';
            i += 2;
            continue;
          }
          i++;
          break;
        }
        out += src[i];
        i++;
      }
      tokens.push({ t: "str", v: out });
      continue;
    }
    if (ch === "'") {
      // 'Quoted sheet name' (must be followed by `!`), `''` = escaped quote.
      let out = "";
      i++;
      for (;;) {
        if (i >= src.length) {
          throw new FormulaError(ERR.parse);
        }
        if (src[i] === "'") {
          if (src[i + 1] === "'") {
            out += "'";
            i += 2;
            continue;
          }
          i++;
          break;
        }
        out += src[i];
        i++;
      }
      tokens.push({ t: "qname", v: out });
      continue;
    }
    if (ch === "!") {
      tokens.push({ t: "bang" });
      i++;
      continue;
    }
    if (ch === "#") {
      // Error literals: only #REF! (left behind by row/column deletions).
      if (src.slice(i, i + 5).toUpperCase() === "#REF!") {
        tokens.push({ t: "errlit", v: ERR.ref });
        i += 5;
        continue;
      }
      throw new FormulaError(ERR.parse);
    }
    if (/[A-Za-z_$]/.test(ch)) {
      const m = /^[$A-Za-z_][$A-Za-z0-9_.]*/.exec(src.slice(i));
      if (!m) {
        throw new FormulaError(ERR.parse);
      }
      tokens.push({ t: "ident", v: m[0] });
      i += m[0].length;
      continue;
    }
    if (ch === "(") {
      tokens.push({ t: "lparen" });
      i++;
      continue;
    }
    if (ch === ")") {
      tokens.push({ t: "rparen" });
      i++;
      continue;
    }
    if (ch === ",") {
      tokens.push({ t: "comma" });
      i++;
      continue;
    }
    if (ch === ":") {
      tokens.push({ t: "colon" });
      i++;
      continue;
    }
    if (ch === "<" && (src[i + 1] === "=" || src[i + 1] === ">")) {
      tokens.push({ t: "op", v: `<${src[i + 1]!}` });
      i += 2;
      continue;
    }
    if (ch === ">" && src[i + 1] === "=") {
      tokens.push({ t: "op", v: ">=" });
      i += 2;
      continue;
    }
    if ("+-*/^%&=<>".includes(ch)) {
      tokens.push({ t: "op", v: ch });
      i++;
      continue;
    }
    throw new FormulaError(ERR.parse);
  }
  return tokens;
}

// ── Parser (recursive descent → AST) ─────────────────────────────────────────

type Ast =
  | { k: "num"; v: number }
  | { k: "str"; v: string }
  | { k: "bool"; v: boolean }
  | { k: "ref"; addr: CellAddr; sheet?: string }
  | { k: "range"; from: CellAddr; to: CellAddr; sheet?: string }
  | { k: "errlit"; code: string }
  | { k: "unary"; op: string; expr: Ast }
  | { k: "binary"; op: string; left: Ast; right: Ast }
  | { k: "call"; name: string; args: Ast[] };

class Parser {
  private pos = 0;
  constructor(private readonly tokens: Token[]) {}

  parse(): Ast {
    const ast = this.comparison();
    if (this.pos < this.tokens.length) {
      throw new FormulaError(ERR.parse);
    }
    return ast;
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }
  private takeOp(...ops: string[]): string | null {
    const tok = this.peek();
    if (tok?.t === "op" && ops.includes(tok.v)) {
      this.pos++;
      return tok.v;
    }
    return null;
  }

  private comparison(): Ast {
    let left = this.concat();
    for (;;) {
      const op = this.takeOp("=", "<>", "<", "<=", ">", ">=");
      if (!op) {
        return left;
      }
      left = { k: "binary", op, left, right: this.concat() };
    }
  }

  private concat(): Ast {
    let left = this.additive();
    for (;;) {
      const op = this.takeOp("&");
      if (!op) {
        return left;
      }
      left = { k: "binary", op, left, right: this.additive() };
    }
  }

  private additive(): Ast {
    let left = this.multiplicative();
    for (;;) {
      const op = this.takeOp("+", "-");
      if (!op) {
        return left;
      }
      left = { k: "binary", op, left, right: this.multiplicative() };
    }
  }

  private multiplicative(): Ast {
    let left = this.unary();
    for (;;) {
      const op = this.takeOp("*", "/");
      if (!op) {
        return left;
      }
      left = { k: "binary", op, left, right: this.unary() };
    }
  }

  private unary(): Ast {
    if (this.takeOp("-")) {
      return { k: "unary", op: "-", expr: this.unary() };
    }
    if (this.takeOp("+")) {
      return this.unary();
    }
    return this.power();
  }

  private power(): Ast {
    const base = this.postfix();
    if (this.takeOp("^")) {
      // Right-associative.
      return { k: "binary", op: "^", left: base, right: this.unary() };
    }
    return base;
  }

  private postfix(): Ast {
    let expr = this.atom();
    while (this.takeOp("%")) {
      expr = { k: "binary", op: "/", left: expr, right: { k: "num", v: 100 } };
    }
    return expr;
  }

  private atom(): Ast {
    const tok = this.peek();
    if (!tok) {
      throw new FormulaError(ERR.parse);
    }
    if (tok.t === "num") {
      this.pos++;
      return { k: "num", v: tok.v };
    }
    if (tok.t === "str") {
      this.pos++;
      return { k: "str", v: tok.v };
    }
    if (tok.t === "lparen") {
      this.pos++;
      const inner = this.comparison();
      if (this.peek()?.t !== "rparen") {
        throw new FormulaError(ERR.parse);
      }
      this.pos++;
      return inner;
    }
    if (tok.t === "errlit") {
      this.pos++;
      return { k: "errlit", code: tok.v };
    }
    if (tok.t === "qname") {
      // 'Sheet name'!REF — quoted names are always sheet prefixes.
      this.pos++;
      if (this.peek()?.t !== "bang") {
        throw new FormulaError(ERR.parse);
      }
      this.pos++;
      return this.sheetRef(tok.v);
    }
    if (tok.t === "ident") {
      this.pos++;

      // Bare sheet prefix: `Sheet2!A1`.
      if (this.peek()?.t === "bang") {
        this.pos++;
        return this.sheetRef(tok.v);
      }

      const upper = tok.v.toUpperCase();
      if (upper === "TRUE") {
        return { k: "bool", v: true };
      }
      if (upper === "FALSE") {
        return { k: "bool", v: false };
      }

      // Function call?
      if (this.peek()?.t === "lparen") {
        this.pos++;
        const args: Ast[] = [];
        if (this.peek()?.t !== "rparen") {
          for (;;) {
            args.push(this.comparison());
            if (this.peek()?.t === "comma") {
              this.pos++;
              continue;
            }
            break;
          }
        }
        if (this.peek()?.t !== "rparen") {
          throw new FormulaError(ERR.parse);
        }
        this.pos++;
        return { k: "call", name: upper, args };
      }

      return this.refOrRange(tok.v, undefined);
    }
    throw new FormulaError(ERR.parse);
  }

  /** After a `!`: the cell ref (or range) the sheet prefix applies to. */
  private sheetRef(sheet: string): Ast {
    const tok = this.peek();
    if (tok?.t !== "ident") {
      throw new FormulaError(ERR.parse);
    }
    this.pos++;
    return this.refOrRange(tok.v, sheet);
  }

  /** Cell ref (possibly a range `A1:B3`), optionally sheet-qualified. */
  private refOrRange(first: string, sheet: string | undefined): Ast {
    const from = parseCellRef(first);
    if (!from) {
      throw new FormulaError(ERR.name);
    }
    if (this.peek()?.t === "colon") {
      this.pos++;
      const next = this.peek();
      if (next?.t !== "ident") {
        throw new FormulaError(ERR.parse);
      }
      const to = parseCellRef(next.v);
      if (!to) {
        throw new FormulaError(ERR.ref);
      }
      this.pos++;
      return {
        k: "range",
        from: { row: Math.min(from.row, to.row), col: Math.min(from.col, to.col) },
        to: { row: Math.max(from.row, to.row), col: Math.max(from.col, to.col) },
        ...(sheet === undefined ? {} : { sheet }),
      };
    }
    return { k: "ref", addr: from, ...(sheet === undefined ? {} : { sheet }) };
  }
}

// ── Evaluator ────────────────────────────────────────────────────────────────

/**
 * Resolves a cell's value during evaluation (throws FormulaError on cycles).
 * `sheet` is set for sheet-qualified refs (`Sheet2!A1`); single-sheet
 * resolvers may ignore it (workbook-level resolution lives in
 * `computeWorkbook`, which throws #REF! for unknown sheet names).
 */
export type CellResolver = (addr: CellAddr, sheet?: string) => Scalar;

function toNumber(v: Scalar): number {
  if (v === null) {
    return 0;
  }
  if (typeof v === "number") {
    return v;
  }
  if (typeof v === "boolean") {
    return v ? 1 : 0;
  }
  const trimmed = v.trim();
  if (trimmed === "") {
    return 0;
  }
  const n = Number(trimmed);
  if (Number.isNaN(n)) {
    throw new FormulaError(ERR.value);
  }
  return n;
}

function toText(v: Scalar): string {
  if (v === null) {
    return "";
  }
  if (typeof v === "boolean") {
    return v ? "TRUE" : "FALSE";
  }
  return String(v);
}

function toBool(v: Scalar): boolean {
  if (v === null) {
    return false;
  }
  if (typeof v === "boolean") {
    return v;
  }
  if (typeof v === "number") {
    return v !== 0;
  }
  const upper = v.trim().toUpperCase();
  if (upper === "TRUE") {
    return true;
  }
  if (upper === "FALSE" || upper === "") {
    return false;
  }
  throw new FormulaError(ERR.value);
}

function compare(op: string, a: Scalar, b: Scalar): boolean {
  let cmp: number;
  if (typeof a === "number" && typeof b === "number") {
    cmp = a === b ? 0 : a < b ? -1 : 1;
  } else if (typeof a === "string" && typeof b === "string") {
    cmp = a.toLowerCase().localeCompare(b.toLowerCase());
  } else if (a === null || b === null) {
    // Empty compares equal to "" and 0 (pragmatic).
    const an = a ?? (typeof b === "string" ? "" : 0);
    const bn = b ?? (typeof a === "string" ? "" : 0);
    return compare(op, an, bn);
  } else {
    // Mixed types: coerce to text (stable, documented).
    cmp = toText(a).toLowerCase().localeCompare(toText(b).toLowerCase());
  }
  switch (op) {
    case "=": {
      return cmp === 0;
    }
    case "<>": {
      return cmp !== 0;
    }
    case "<": {
      return cmp < 0;
    }
    case "<=": {
      return cmp <= 0;
    }
    case ">": {
      return cmp > 0;
    }
    case ">=": {
      return cmp >= 0;
    }
    default: {
      throw new FormulaError(ERR.parse);
    }
  }
}

/** Flattens args: ranges expand to their cells, scalars pass through. */
function flattenToScalars(args: Ast[], resolve: CellResolver): Scalar[] {
  const out: Scalar[] = [];
  for (const arg of args) {
    if (arg.k === "range") {
      for (let r = arg.from.row; r <= arg.to.row; r++) {
        for (let c = arg.from.col; c <= arg.to.col; c++) {
          out.push(resolve({ row: r, col: c }, arg.sheet));
        }
      }
    } else {
      out.push(evalAst(arg, resolve));
    }
  }
  return out;
}

function aggregateNumbers(args: Ast[], resolve: CellResolver): number[] {
  // Aggregates skip empty cells and non-numeric text (Excel semantics).
  return flattenToScalars(args, resolve)
    .filter((v): v is number | boolean => typeof v === "number" || typeof v === "boolean")
    .map((v) => (typeof v === "boolean" ? (v ? 1 : 0) : v));
}

function evalCall(node: Extract<Ast, { k: "call" }>, resolve: CellResolver): Scalar {
  const { name, args } = node;
  switch (name) {
    case "SUM": {
      return aggregateNumbers(args, resolve).reduce((a, b) => a + b, 0);
    }
    case "AVERAGE": {
      const nums = aggregateNumbers(args, resolve);
      if (nums.length === 0) {
        throw new FormulaError(ERR.div0);
      }
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    }
    case "MIN": {
      const nums = aggregateNumbers(args, resolve);
      return nums.length === 0 ? 0 : Math.min(...nums);
    }
    case "MAX": {
      const nums = aggregateNumbers(args, resolve);
      return nums.length === 0 ? 0 : Math.max(...nums);
    }
    case "COUNT": {
      return flattenToScalars(args, resolve).filter((v) => typeof v === "number").length;
    }
    case "COUNTA": {
      return flattenToScalars(args, resolve).filter((v) => v !== null && v !== "").length;
    }
    case "IF": {
      if (args.length < 2 || args.length > 3) {
        throw new FormulaError(ERR.value);
      }
      const cond = toBool(evalAst(args[0]!, resolve));
      if (cond) {
        return evalAst(args[1]!, resolve);
      }
      return args.length === 3 ? evalAst(args[2]!, resolve) : false;
    }
    case "ABS": {
      if (args.length !== 1) {
        throw new FormulaError(ERR.value);
      }
      return Math.abs(toNumber(evalAst(args[0]!, resolve)));
    }
    case "ROUND": {
      if (args.length === 0 || args.length > 2) {
        throw new FormulaError(ERR.value);
      }
      const value = toNumber(evalAst(args[0]!, resolve));
      const digits = args.length === 2 ? toNumber(evalAst(args[1]!, resolve)) : 0;
      const factor = 10 ** Math.trunc(digits);
      return Math.round(value * factor) / factor;
    }
    default: {
      throw new FormulaError(ERR.name);
    }
  }
}

function evalAst(node: Ast, resolve: CellResolver): Scalar {
  switch (node.k) {
    case "num": {
      return node.v;
    }
    case "str": {
      return node.v;
    }
    case "bool": {
      return node.v;
    }
    case "ref": {
      return resolve(node.addr, node.sheet);
    }
    case "range": {
      // A bare range outside an aggregate argument has no scalar value.
      throw new FormulaError(ERR.value);
    }
    case "errlit": {
      throw new FormulaError(node.code);
    }
    case "call": {
      return evalCall(node, resolve);
    }
    case "unary": {
      return -toNumber(evalAst(node.expr, resolve));
    }
    case "binary": {
      const { op } = node;
      if (op === "&") {
        return toText(evalAst(node.left, resolve)) + toText(evalAst(node.right, resolve));
      }
      if (["=", "<>", "<", "<=", ">", ">="].includes(op)) {
        return compare(op, evalAst(node.left, resolve), evalAst(node.right, resolve));
      }
      const a = toNumber(evalAst(node.left, resolve));
      const b = toNumber(evalAst(node.right, resolve));
      switch (op) {
        case "+": {
          return a + b;
        }
        case "-": {
          return a - b;
        }
        case "*": {
          return a * b;
        }
        case "/": {
          if (b === 0) {
            throw new FormulaError(ERR.div0);
          }
          return a / b;
        }
        case "^": {
          return a ** b;
        }
        default: {
          throw new FormulaError(ERR.parse);
        }
      }
    }
    default: {
      throw new FormulaError(ERR.parse);
    }
  }
}

/**
 * Evaluates one formula (WITHOUT the leading `=`) against a cell resolver.
 * Throws `FormulaError` with an Excel-style code on any failure.
 */
export function evaluateFormula(expr: string, resolve: CellResolver): Scalar {
  return evalAst(new Parser(tokenize(expr)).parse(), resolve);
}

// ── Sheet-level computation (deps + cycles) ──────────────────────────────────

import type { Cell, Sheet, Workbook } from "./office-io";

/** A computed cell, ready for display. */
export interface ComputedCell {
  /** Display string (formatted value or error code). */
  display: string;
  /** Raw scalar (null for empty), used by dependent formulas. */
  value: Scalar;
  /** Error code when the formula failed (display === error). */
  error?: string;
  /** True when this cell holds a formula. */
  isFormula: boolean;
}

function cellScalar(cell: Cell | undefined): Scalar {
  const v = cell?.value;
  if (!v || v.t === "empty") {
    return null;
  }
  return v.v;
}

export function formatScalar(v: Scalar): string {
  if (v === null) {
    return "";
  }
  if (typeof v === "boolean") {
    return v ? "TRUE" : "FALSE";
  }
  if (typeof v === "number") {
    if (!Number.isFinite(v)) {
      return ERR.value;
    }
    // Trim float noise without losing precision for typical sheet values.
    const rounded = Math.round(v * 1e10) / 1e10;
    return String(rounded);
  }
  return v;
}

/** Sheet-name lookup key: names match case-insensitively (Excel semantics). */
export function sheetNameKey(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Computes every cell of every sheet of a workbook: formulas are evaluated
 * with workbook-wide memoisation and cycle detection (cycles across sheets are
 * caught too); plain cells pass through. Sheet-qualified refs (`Sheet2!A1`)
 * resolve against the workbook by case-insensitive name — unknown names are
 * `#REF!`. Returns one map per sheet (parallel to `workbook.sheets`), keyed
 * `row,col` (only non-empty/computed cells present).
 */
export function computeWorkbook(workbook: Workbook): Map<string, ComputedCell>[] {
  const memo = new Map<string, Scalar>();
  const inFlight = new Set<string>();
  const byName = new Map<string, number>();
  workbook.sheets.forEach((s, i) => byName.set(sheetNameKey(s.name), i));

  const resolveIn =
    (sheetIdx: number): CellResolver =>
    (addr, sheetRef) => {
      let idx = sheetIdx;
      if (sheetRef !== undefined) {
        const found = byName.get(sheetNameKey(sheetRef));
        if (found === undefined) {
          throw new FormulaError(ERR.ref);
        }
        idx = found;
      }
      const key = `${idx}:${addr.row},${addr.col}`;
      if (memo.has(key)) {
        return memo.get(key) ?? null;
      }
      if (inFlight.has(key)) {
        throw new FormulaError(ERR.cycle);
      }
      const cell = workbook.sheets[idx]?.rows[addr.row]?.[addr.col];
      if (!cell?.formula) {
        const value = cellScalar(cell);
        memo.set(key, value);
        return value;
      }
      inFlight.add(key);
      try {
        const value = evaluateFormula(cell.formula, resolveIn(idx));
        memo.set(key, value);
        return value;
      } finally {
        inFlight.delete(key);
      }
    };

  return workbook.sheets.map((sheet, idx) => {
    const out = new Map<string, ComputedCell>();
    const resolve = resolveIn(idx);
    sheet.rows.forEach((row, r) => {
      row.forEach((cell, c) => {
        const isFormula = Boolean(cell.formula);
        if (!isFormula && cellScalar(cell) === null) {
          return;
        }
        try {
          const value = resolve({ row: r, col: c });
          out.set(`${r},${c}`, { display: formatScalar(value), value, isFormula });
        } catch (err) {
          const code = err instanceof FormulaError ? err.code : ERR.parse;
          out.set(`${r},${c}`, { display: code, value: null, error: code, isFormula });
        }
      });
    });
    return out;
  });
}

/**
 * Computes every cell of a standalone sheet (workbook computation restricted
 * to one sheet — sheet-qualified refs to other sheets come out `#REF!`).
 */
export function computeSheet(sheet: Sheet): Map<string, ComputedCell> {
  return computeWorkbook({ sheets: [sheet] })[0]!;
}
